/* ============================================================
 * gwaw-compute.js
 * 3段階フォールバック計算ライブラリ（WebGPU → WebGL2 → CPU）
 * 独立並列（embarrassingly parallel）計算に特化
 *
 * 単一ファイル・依存なし。<script src> で読み込んで使用。
 *   window.GwawCompute としてエクスポート（ES module / global 両対応）
 *
 * Copyright (c) Shinji Sasagawa — gwaw.jp / iseeit.jp
 * ============================================================ */
(function (global) {
'use strict';

/* ------------------------------------------------------------
 * 型システム / バッファレイアウト
 * ----------------------------------------------------------*/
const TYPE_SIZE = { u32: 4, i32: 4, f32: 4 };
const TYPED_ARRAY = { u32: Uint32Array, i32: Int32Array, f32: Float32Array };

/**
 * params 宣言から uniform バッファのバイトレイアウトを生成する。
 * WebGPU の std140 ライクな 16byte アライメントには踏み込まず、
 * 「全要素を 4byte スカラとして隙間なく詰める」単純規約を採用する。
 * （独立並列カーネルの uniform は小さく、スカラ主体のため十分）
 * シェーダ側も同じ並びの struct を宣言する前提。
 */
class BufferLayout {
  constructor(params) {
    this.keys = Object.keys(params);
    this.entries = this.keys.map((k, i) => ({
      name: k, type: params[k].type, offset: i * 4,
    }));
    // WebGPU の uniform は構造体全体が 16byte 境界。4byte刻みで詰めた上で
    // 全体サイズを 16 の倍数に切り上げる（末尾パディング）
    const raw = Math.max(16, this.keys.length * 4);
    this.byteLength = Math.ceil(raw / 16) * 16;
  }
  /** params の現在値を ArrayBuffer に書き込む */
  pack(params) {
    const buf = new ArrayBuffer(this.byteLength);
    const u32 = new Uint32Array(buf);
    const f32 = new Float32Array(buf);
    const i32 = new Int32Array(buf);
    this.entries.forEach((e, idx) => {
      const v = params[e.name].value;
      if (e.type === 'u32') u32[idx] = v >>> 0;
      else if (e.type === 'i32') i32[idx] = v | 0;
      else f32[idx] = v;
    });
    return buf;
  }
}

/* ------------------------------------------------------------
 * PCG 乱数（3バックエンド共通のロジック）
 * JS実装。WGSL/GLSL側は spec のシェーダ内に同等実装を持つ。
 * ----------------------------------------------------------*/
class PCGRandom {
  constructor(seed) { this.state = (seed >>> 0) || 0x853c49e3; }
  next() {
    let x = this.state >>> 0;
    this.state = (Math.imul(x, 747796405) + 2891336453) >>> 0;
    let word = (Math.imul((x >>> ((x >>> 28) + 4)) ^ x, 277803737)) >>> 0;
    return ((word >>> 22) ^ word) >>> 0;
  }
  float() { return this.next() / 4294967296; }
  normal() { // Box-Muller
    const u1 = this.float() + 1e-10, u2 = this.float();
    return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  }
}

/* ------------------------------------------------------------
 * 共通ユーティリティ
 * ----------------------------------------------------------*/
function makeSeeds(n) {
  const a = new Uint32Array(n);
  for (let i = 0; i < n; i++) a[i] = (Math.random() * 4294967296) >>> 0;
  return a;
}

/* ============================================================
 * CPU バックエンド（フォールバック基準・最も単純）
 * ============================================================ */
class CPUBackend {
  static get name() { return 'CPU'; }
  isAvailable() { return true; }
  async init() { /* 何もしない */ }

  async execute(spec, nThreads, opts) {
    const t0 = performance.now();
    const outBufs = {};
    for (const k in spec.outputs) {
      const o = spec.outputs[k];
      outBufs[k] = new TYPED_ARRAY[o.type](nThreads * (o.perThread || 1));
    }
    // 入力（seed等）を生成
    const inBufs = {};
    for (const k in (spec.inputs || {})) {
      const inp = spec.inputs[k];
      const arr = new TYPED_ARRAY[inp.type](nThreads * (inp.perThread || 1));
      for (let i = 0; i < arr.length; i++) arr[i] = inp.init ? inp.init(i) : 0;
      inBufs[k] = arr;
    }

    const params = spec.params;
    const BATCH = 2000;
    for (let i = 0; i < nThreads; i += BATCH) {
      const end = Math.min(i + BATCH, nThreads);
      for (let tid = i; tid < end; tid++) {
        // 各スレッドの CPU 関数を呼ぶ。rng は seed から生成
        const seed = inBufs.seeds ? inBufs.seeds[tid] : (tid * 2654435761) >>> 0;
        const rng = new PCGRandom(seed);
        const res = spec.cpu(params, tid, rng, inBufs);
        for (const k in spec.outputs) {
          const pt = spec.outputs[k].perThread || 1;
          if (pt === 1) outBufs[k][tid] = res[k];
          else for (let j = 0; j < pt; j++) outBufs[k][tid * pt + j] = res[k][j];
        }
      }
      if (opts.onProgress) opts.onProgress(Math.round((end / nThreads) * 100));
      if (i % (BATCH * 4) === 0) await new Promise(r => setTimeout(r, 0));
    }
    return { outputs: outBufs, elapsed: performance.now() - t0, backend: 'CPU' };
  }
  dispose() {}
}

/* ============================================================
 * WebGPU バックエンド
 * ============================================================ */
class WebGPUBackend {
  static get name() { return 'WebGPU'; }
  constructor() { this.device = null; this._pipelines = new Map(); }

  isAvailable() { return !!this.device; }

  async init() {
    if (this.device) return true;
    if (!global.navigator || !global.navigator.gpu) return false;
    try {
      const adapter = await navigator.gpu.requestAdapter();
      if (!adapter) return false;
      this.device = await adapter.requestDevice();
      this.adapterInfo = adapter.info || {};
      return true;
    } catch (e) { return false; }
  }

  _getPipeline(spec) {
    if (this._pipelines.has(spec.name)) return this._pipelines.get(spec.name);
    const module = this.device.createShaderModule({ code: spec.wgsl });
    const pipeline = this.device.createComputePipeline({
      layout: 'auto', compute: { module, entryPoint: 'main' },
    });
    this._pipelines.set(spec.name, pipeline);
    return pipeline;
  }

  async execute(spec, nThreads, opts) {
    const dev = this.device;
    const t0 = performance.now();
    const layout = new BufferLayout(spec.params);

    // uniform
    const paramBuf = dev.createBuffer({
      size: layout.byteLength,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
    dev.queue.writeBuffer(paramBuf, 0, layout.pack(spec.params));

    // 入力バッファ（binding 1..）
    const created = [paramBuf];
    const bindings = [{ binding: 0, resource: { buffer: paramBuf } }];
    let b = 1;
    const inKeys = Object.keys(spec.inputs || {});
    for (const k of inKeys) {
      const inp = spec.inputs[k];
      const arr = new TYPED_ARRAY[inp.type](nThreads * (inp.perThread || 1));
      for (let i = 0; i < arr.length; i++) arr[i] = inp.init ? inp.init(i) : 0;
      const buf = dev.createBuffer({
        size: arr.byteLength,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      });
      dev.queue.writeBuffer(buf, 0, arr);
      bindings.push({ binding: b++, resource: { buffer: buf } });
      created.push(buf);
    }

    // 出力バッファ
    const outKeys = Object.keys(spec.outputs);
    const outGpu = {}, outRead = {};
    for (const k of outKeys) {
      const o = spec.outputs[k];
      const bytes = nThreads * (o.perThread || 1) * TYPE_SIZE[o.type];
      const gbuf = dev.createBuffer({ size: bytes, usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC });
      const rbuf = dev.createBuffer({ size: bytes, usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ });
      bindings.push({ binding: b++, resource: { buffer: gbuf } });
      outGpu[k] = gbuf; outRead[k] = rbuf; created.push(gbuf, rbuf);
    }

    const pipeline = this._getPipeline(spec);
    const bindGroup = dev.createBindGroup({ layout: pipeline.getBindGroupLayout(0), entries: bindings });

    // dispatch（65535 上限対策：grid-stride はシェーダ側で吸収する前提で、
    //  ここでは workgroup 数を上限内に収める）
    const wgSize = spec.workgroupSize || 64;
    let groups = Math.ceil(nThreads / wgSize);
    const MAX = 65535;
    const encoder = dev.createCommandEncoder();
    const pass = encoder.beginComputePass();
    pass.setPipeline(pipeline);
    pass.setBindGroup(0, bindGroup);
    if (groups <= MAX) {
      pass.dispatchWorkgroups(groups);
    } else {
      // 2次元に分割
      const x = MAX, y = Math.ceil(groups / MAX);
      pass.dispatchWorkgroups(x, y);
    }
    pass.end();
    for (const k of outKeys) {
      encoder.copyBufferToBuffer(outGpu[k], 0, outRead[k], 0, outRead[k].size);
    }
    dev.queue.submit([encoder.finish()]);
    if (opts.onProgress) opts.onProgress(70);

    const outputs = {};
    for (const k of outKeys) {
      await outRead[k].mapAsync(GPUMapMode.READ);
      outputs[k] = new TYPED_ARRAY[spec.outputs[k].type](outRead[k].getMappedRange()).slice();
      outRead[k].unmap();
    }
    created.forEach(buf => buf.destroy());
    return { outputs, elapsed: performance.now() - t0, backend: 'WebGPU' };
  }

  dispose() { this._pipelines.clear(); }
}

/* ============================================================
 * WebGL2 バックエンド（transform feedback）
 * ============================================================ */
class WebGL2Backend {
  static get name() { return 'WebGL2'; }
  constructor() { this.gl = null; this._programs = new Map(); }

  isAvailable() { return !!this.gl; }

  async init() {
    if (this.gl) return true;
    try {
      const canvas = (global.document)
        ? global.document.createElement('canvas')
        : new OffscreenCanvas(1, 1);
      const gl = canvas.getContext('webgl2');
      if (!gl) return false;
      if (!gl.getExtension('EXT_color_buffer_float')) return false;
      this.gl = gl;
      return true;
    } catch (e) { return false; }
  }

  _getProgram(spec) {
    if (this._programs.has(spec.name)) return this._programs.get(spec.name);
    const gl = this.gl;
    const vs = gl.createShader(gl.VERTEX_SHADER);
    gl.shaderSource(vs, spec.glsl); gl.compileShader(vs);
    if (!gl.getShaderParameter(vs, gl.COMPILE_STATUS))
      throw new Error('VS compile: ' + gl.getShaderInfoLog(vs));
    const fs = gl.createShader(gl.FRAGMENT_SHADER);
    gl.shaderSource(fs, '#version 300 es\nprecision highp float;\nout vec4 o;\nvoid main(){o=vec4(0.0);}');
    gl.compileShader(fs);
    const prog = gl.createProgram();
    gl.attachShader(prog, vs); gl.attachShader(prog, fs);
    // 出力 varying（outputs の宣言順、prefix v_）
    const varyings = Object.keys(spec.outputs).map(k => 'v_' + k);
    gl.transformFeedbackVaryings(prog, varyings, gl.SEPARATE_ATTRIBS);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS))
      throw new Error('Link: ' + gl.getProgramInfoLog(prog));
    this._programs.set(spec.name, prog);
    return prog;
  }

  async execute(spec, nThreads, opts) {
    const gl = this.gl;
    const t0 = performance.now();
    const prog = this._getProgram(spec);
    gl.useProgram(prog);

    // uniform 設定
    for (const k in spec.params) {
      const p = spec.params[k];
      const loc = gl.getUniformLocation(prog, 'u_' + k);
      if (loc === null) continue;
      if (p.type === 'u32') gl.uniform1ui(loc, p.value >>> 0);
      else if (p.type === 'i32') gl.uniform1i(loc, p.value | 0);
      else gl.uniform1f(loc, p.value);
    }

    const vao = gl.createVertexArray();
    gl.bindVertexArray(vao);
    const created = [];

    // 入力（seed等）を attribute として供給
    const inKeys = Object.keys(spec.inputs || {});
    for (const k of inKeys) {
      const inp = spec.inputs[k];
      const arr = new TYPED_ARRAY[inp.type](nThreads * (inp.perThread || 1));
      for (let i = 0; i < arr.length; i++) arr[i] = inp.init ? inp.init(i) : 0;
      const buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, arr, gl.STATIC_DRAW);
      const loc = gl.getAttribLocation(prog, 'a_' + k);
      if (loc >= 0) {
        gl.enableVertexAttribArray(loc);
        if (inp.type === 'f32') gl.vertexAttribPointer(loc, 1, gl.FLOAT, false, 0, 0);
        else gl.vertexAttribIPointer(loc, 1, inp.type === 'u32' ? gl.UNSIGNED_INT : gl.INT, 0, 0);
      }
      created.push(buf);
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, null);

    // 出力バッファ
    const outKeys = Object.keys(spec.outputs);
    const outBuf = {};
    const tf = gl.createTransformFeedback();
    gl.bindTransformFeedback(gl.TRANSFORM_FEEDBACK, tf);
    outKeys.forEach((k, i) => {
      const o = spec.outputs[k];
      const bytes = nThreads * (o.perThread || 1) * TYPE_SIZE[o.type];
      const buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, bytes, gl.DYNAMIC_COPY);
      gl.bindBufferBase(gl.TRANSFORM_FEEDBACK_BUFFER, i, buf);
      outBuf[k] = buf; created.push(buf);
    });
    gl.bindBuffer(gl.ARRAY_BUFFER, null);

    gl.enable(gl.RASTERIZER_DISCARD);
    gl.beginTransformFeedback(gl.POINTS);
    gl.drawArrays(gl.POINTS, 0, nThreads);
    gl.endTransformFeedback();
    gl.disable(gl.RASTERIZER_DISCARD);
    outKeys.forEach((k, i) => gl.bindBufferBase(gl.TRANSFORM_FEEDBACK_BUFFER, i, null));
    gl.bindTransformFeedback(gl.TRANSFORM_FEEDBACK, null);
    gl.flush();
    if (opts.onProgress) opts.onProgress(70);

    gl.bindVertexArray(null);
    const outputs = {};
    for (const k of outKeys) {
      const o = spec.outputs[k];
      const arr = new TYPED_ARRAY[o.type](nThreads * (o.perThread || 1));
      gl.bindBuffer(gl.ARRAY_BUFFER, outBuf[k]);
      gl.getBufferSubData(gl.ARRAY_BUFFER, 0, arr);
      outputs[k] = arr;
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
    created.forEach(b => gl.deleteBuffer(b));
    gl.deleteTransformFeedback(tf);
    gl.deleteVertexArray(vao);
    return { outputs, elapsed: performance.now() - t0, backend: 'WebGL2' };
  }

  dispose() { this._programs.clear(); }
}

/* ============================================================
 * GwawCompute（公開ファサード）
 * ============================================================ */
class GwawCompute {
  constructor() {
    this.backends = {
      webgpu: new WebGPUBackend(),
      webgl2: new WebGL2Backend(),
      cpu: new CPUBackend(),
    };
    this.capabilities = { webgpu: false, webgl2: false, cpu: true };
    this.selected = 'auto';
    this._detected = false;
  }

  /** 能力検出（一度だけ実行すればよい） */
  async detect() {
    this.capabilities.webgpu = await this.backends.webgpu.init();
    this.capabilities.webgl2 = await this.backends.webgl2.init();
    this.capabilities.cpu = true;
    this._detected = true;
    return this.capabilities;
  }

  /** 'auto' | 'webgpu' | 'webgl2' | 'cpu' */
  setBackend(name) {
    if (name !== 'auto' && !this.capabilities[name])
      throw new Error(`backend not available: ${name}`);
    this.selected = name;
  }

  /** 自動選択時の実効バックエンド名 */
  resolveBackend() {
    if (this.selected !== 'auto') return this.selected;
    if (this.capabilities.webgpu) return 'webgpu';
    if (this.capabilities.webgl2) return 'webgl2';
    return 'cpu';
  }

  backendLabel() {
    const m = { webgpu: 'WebGPU', webgl2: 'WebGL2', cpu: 'CPU' };
    return m[this.resolveBackend()];
  }

  /**
   * 計算実行。最適バックエンドを選び、失敗時は下位へフォールバック。
   * opts: { nThreads, onProgress, allowFallback=true, validate }
   */
  async run(spec, opts = {}) {
    if (!this._detected) await this.detect();
    const nThreads = opts.nThreads || 10000;
    const order = this._fallbackOrder();
    const validate = opts.validate || spec.validate || (() => true);
    let lastErr = null;

    for (const beName of order) {
      const be = this.backends[beName];
      if (!be.isAvailable()) continue;
      try {
        const res = await be.execute(spec, nThreads, opts);
        if (!validate(res.outputs, spec, nThreads)) {
          lastErr = new Error(`${beName}: validation failed`);
          if (opts.allowFallback === false) throw lastErr;
          continue; // 次のバックエンドへ
        }
        if (opts.onProgress) opts.onProgress(100);
        return res;
      } catch (e) {
        lastErr = e;
        if (opts.allowFallback === false) throw e;
        // 次のバックエンドへ
      }
    }
    throw lastErr || new Error('all backends failed');
  }

  /** 選択に基づくフォールバック順序 */
  _fallbackOrder() {
    if (this.selected === 'auto') return ['webgpu', 'webgl2', 'cpu'];
    // 手動選択時：選択 → それ以下へ
    const chain = { webgpu: ['webgpu', 'webgl2', 'cpu'], webgl2: ['webgl2', 'cpu'], cpu: ['cpu'] };
    return chain[this.selected] || ['cpu'];
  }

  /**
   * scaling ベンチマーク。
   * opts: { sizes, backends, cpuMaxSize, onProgress }
   */
  async benchmark(spec, opts = {}) {
    if (!this._detected) await this.detect();
    const sizes = opts.sizes || [1000, 5000, 10000, 50000, 100000];
    const want = opts.backends || ['webgpu', 'webgl2', 'cpu'];
    const cpuMax = opts.cpuMaxSize || 50000;
    const result = { sizes };
    want.forEach(b => result[b] = []);

    for (const size of sizes) {
      for (const beName of want) {
        const be = this.backends[beName];
        const avail = this.capabilities[beName] && be.isAvailable();
        if (!avail || (beName === 'cpu' && size > cpuMax)) {
          result[beName].push(null);
          continue;
        }
        try {
          const res = await be.execute(spec, size, {});
          result[beName].push(+res.elapsed.toFixed(2));
        } catch (e) {
          result[beName].push(null);
        }
        if (opts.onProgress) opts.onProgress(beName, size);
        await new Promise(r => setTimeout(r, 5));
      }
    }
    return result;
  }

  /* ========================================================
   *  行列積 C = A(M×K) × B(K×N)  → C(M×N)（row-major）
   *  方向B（線形代数のGPU実装）。WebGPU / CPU をサポート。
   *  opts: { backend:'auto'|'webgpu'|'cpu', tiled:true, onProgress }
   * ======================================================== */
  async matmul(A, B, M, K, N, opts = {}) {
    if (!this._detected) await this.detect();
    const want = opts.backend || (this.capabilities.webgpu ? 'webgpu' : 'cpu');
    if (want === 'webgpu' && this.capabilities.webgpu) {
      try { return await this._matmulWebGPU(A, B, M, K, N, opts); }
      catch (e) { if (opts.allowFallback === false) throw e; return this._matmulCPU(A, B, M, K, N); }
    }
    return this._matmulCPU(A, B, M, K, N);
  }

  _matmulCPU(A, B, M, K, N) {
    const t0 = performance.now();
    const C = new Float32Array(M * N);
    for (let i = 0; i < M; i++) {
      for (let j = 0; j < N; j++) {
        let s = 0;
        for (let k = 0; k < K; k++) s += A[i*K + k] * B[k*N + j];
        C[i*N + j] = s;
      }
    }
    return { C, elapsed: performance.now() - t0, backend: 'CPU' };
  }

  async _matmulWebGPU(A, B, M, K, N, opts) {
    const dev = this.backends.webgpu.device;
    if (!dev) throw new Error('no webgpu device');
    // variant: 'naive' | 'tiled' | 'regblock'
    let variant = opts.variant || (opts.tiled === false ? 'naive' : 'tiled');
    const TS = opts.tileSize || 32;       // タイルサイズ（実測でモバイルGPUは32が最速）
    const TM = opts.tm || 2, TN = opts.tn || 2;  // regblock: 1スレッドが担当する出力ブロック
    const t0 = performance.now();

    const dims = new Uint32Array([M, K, N, 0]);
    const dimBuf = dev.createBuffer({ size: 16, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST });
    dev.queue.writeBuffer(dimBuf, 0, dims);

    const mkStorage = (arr) => {
      const buf = dev.createBuffer({ size: arr.byteLength, usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST });
      dev.queue.writeBuffer(buf, 0, arr); return buf;
    };
    const aBuf = mkStorage(A instanceof Float32Array ? A : new Float32Array(A));
    const bBuf = mkStorage(B instanceof Float32Array ? B : new Float32Array(B));
    const cBytes = M * N * 4;
    const cBuf = dev.createBuffer({ size: cBytes, usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC });
    const cRead = dev.createBuffer({ size: cBytes, usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ });

    let shader, cacheKey;
    if (variant === 'naive') { shader = GwawCompute._WGSL_MATMUL_NAIVE; cacheKey = '__mm_naive'; }
    else if (variant === 'regblock') { shader = GwawCompute._wgslMatmulRegBlock(TS, TM, TN); cacheKey = `__mm_rb_${TS}_${TM}_${TN}`; }
    else { shader = GwawCompute._wgslMatmulTiled(TS); cacheKey = `__mm_tiled_${TS}`; }

    if (!this._mmPipelines) this._mmPipelines = {};
    if (!this._mmPipelines[cacheKey]) {
      const module = dev.createShaderModule({ code: shader });
      this._mmPipelines[cacheKey] = dev.createComputePipeline({ layout:'auto', compute:{ module, entryPoint:'main' } });
    }
    const pipeline = this._mmPipelines[cacheKey];
    const bindGroup = dev.createBindGroup({ layout: pipeline.getBindGroupLayout(0), entries:[
      { binding:0, resource:{ buffer:dimBuf } },
      { binding:1, resource:{ buffer:aBuf } },
      { binding:2, resource:{ buffer:bBuf } },
      { binding:3, resource:{ buffer:cBuf } },
    ]});

    const enc = dev.createCommandEncoder();
    const pass = enc.beginComputePass();
    pass.setPipeline(pipeline);
    pass.setBindGroup(0, bindGroup);
    // ワークグループ数：出力をタイル単位で分割（タイル一辺=TS、naiveは16）
    const tile = (variant === 'naive') ? 16 : TS;
    const gx = Math.ceil(N / tile), gy = Math.ceil(M / tile);
    pass.dispatchWorkgroups(gx, gy);
    pass.end();
    enc.copyBufferToBuffer(cBuf, 0, cRead, 0, cBytes);
    dev.queue.submit([enc.finish()]);

    await cRead.mapAsync(GPUMapMode.READ);
    const C = new Float32Array(cRead.getMappedRange()).slice();
    cRead.unmap();
    [dimBuf, aBuf, bBuf, cBuf, cRead].forEach(b => b.destroy());
    const label = variant==='naive' ? 'WebGPU(naive)' : variant==='regblock' ? `WebGPU(reg ${TS}/${TM}×${TN})` : `WebGPU(tiled ${TS})`;
    return { C, elapsed: performance.now() - t0, backend: label };
  }

  /* 行列積ベンチマーク：sizes は正方行列の一辺 */
  async benchmarkMatmul(opts = {}) {
    if (!this._detected) await this.detect();
    const sizes = opts.sizes || [64, 128, 256, 512, 1024];
    const cpuMax = opts.cpuMaxSize || 512;
    const result = { sizes, cpu:[], webgpuNaive:[], webgpuTiled:[] };
    for (const n of sizes) {
      const A = new Float32Array(n*n), B = new Float32Array(n*n);
      for (let i=0;i<n*n;i++){ A[i]=Math.random(); B[i]=Math.random(); }
      // CPU
      if (n <= cpuMax) { const r = this._matmulCPU(A,B,n,n,n); result.cpu.push(+r.elapsed.toFixed(2)); }
      else result.cpu.push(null);
      // WebGPU naive
      if (this.capabilities.webgpu) {
        try { const r = await this._matmulWebGPU(A,B,n,n,n,{tiled:false}); result.webgpuNaive.push(+r.elapsed.toFixed(2)); }
        catch(e){ result.webgpuNaive.push(null); }
        try { const r = await this._matmulWebGPU(A,B,n,n,n,{tiled:true}); result.webgpuTiled.push(+r.elapsed.toFixed(2)); }
        catch(e){ result.webgpuTiled.push(null); }
      } else { result.webgpuNaive.push(null); result.webgpuTiled.push(null); }
      if (opts.onProgress) opts.onProgress(n);
      await new Promise(r=>setTimeout(r,5));
    }
    return result;
  }

  /* タイル化の深掘りベンチ：variant×tileSize を比較 */
  async benchmarkMatmulVariants(opts = {}) {
    if (!this._detected) await this.detect();
    if (!this.capabilities.webgpu) return null;
    const sizes = opts.sizes || [256, 512, 1024];
    // 比較する構成
    const configs = opts.configs || [
      { name:'naive',      variant:'naive' },
      { name:'tiled-8',    variant:'tiled', tileSize:8 },
      { name:'tiled-16',   variant:'tiled', tileSize:16 },
      { name:'tiled-32',   variant:'tiled', tileSize:32 },
      { name:'reg-32/2×2', variant:'regblock', tileSize:32, tm:2, tn:2 },
    ];
    const result = { sizes, configs: configs.map(c=>c.name), data: {} };
    configs.forEach(c => result.data[c.name] = []);
    for (const n of sizes) {
      const A = new Float32Array(n*n), B = new Float32Array(n*n);
      for (let i=0;i<n*n;i++){ A[i]=Math.random(); B[i]=Math.random(); }
      for (const c of configs) {
        try {
          // 1回ウォームアップ＋3回計測の最小値（ばらつき低減）
          await this._matmulWebGPU(A,B,n,n,n,c);
          let best = Infinity;
          for (let rep=0; rep<3; rep++){ const r = await this._matmulWebGPU(A,B,n,n,n,c); best = Math.min(best, r.elapsed); }
          result.data[c.name].push(+best.toFixed(2));
        } catch(e) { result.data[c.name].push(null); }
        if (opts.onProgress) opts.onProgress(n, c.name);
        await new Promise(r=>setTimeout(r,5));
      }
    }
    return result;
  }

  /* ========================================================
   *  共分散行列 Σ = (1/(n-1)) X'ᵀX'   （X' は列ごと中心化）
   *  X: n×m（n期間 m資産, row-major）→ Σ: m×m
   *  転置積を直接計算（Xを明示転置しない）。tiled-32 を活用。
   *  opts: { backend:'auto'|'webgpu'|'cpu', ddof:1 }
   * ======================================================== */
  async covariance(X, n, m, opts = {}) {
    if (!this._detected) await this.detect();
    const ddof = opts.ddof != null ? opts.ddof : 1;  // 1=不偏(n-1), 0=標本(n)
    const want = opts.backend || (this.capabilities.webgpu ? 'webgpu' : 'cpu');
    if (want === 'webgpu' && this.capabilities.webgpu) {
      try { return await this._covarianceWebGPU(X, n, m, ddof, opts); }
      catch (e) { if (opts.allowFallback === false) throw e; return this._covarianceCPU(X, n, m, ddof); }
    }
    return this._covarianceCPU(X, n, m, ddof);
  }

  _covarianceCPU(X, n, m, ddof) {
    const t0 = performance.now();
    const mean = new Float64Array(m);
    for (let t=0;t<n;t++) for (let j=0;j<m;j++) mean[j] += X[t*m+j];
    for (let j=0;j<m;j++) mean[j] /= n;
    const S = new Float32Array(m*m);
    const denom = n - ddof;
    for (let i=0;i<m;i++){
      for (let j=0;j<m;j++){
        let s=0;
        for (let t=0;t<n;t++) s += (X[t*m+i]-mean[i])*(X[t*m+j]-mean[j]);
        S[i*m+j] = s/denom;
      }
    }
    return { Sigma: S, mean: Float32Array.from(mean), elapsed: performance.now()-t0, backend:'CPU' };
  }

  async _covarianceWebGPU(X, n, m, ddof, opts) {
    const dev = this.backends.webgpu.device;
    if (!dev) throw new Error('no webgpu device');
    const TS = opts.tileSize || 32;
    const t0 = performance.now();

    // 列平均をCPUで計算（mは資産数で小さい。GPU化の価値が薄い）
    const mean = new Float32Array(m);
    { const acc = new Float64Array(m);
      for (let t=0;t<n;t++) for (let j=0;j<m;j++) acc[j]+=X[t*m+j];
      for (let j=0;j<m;j++) mean[j]=acc[j]/n; }

    const params = new Uint32Array([n, m, ddof, 0]);
    const pBuf = dev.createBuffer({ size:16, usage:GPUBufferUsage.UNIFORM|GPUBufferUsage.COPY_DST });
    dev.queue.writeBuffer(pBuf, 0, params);
    const xData = X instanceof Float32Array ? X : new Float32Array(X);
    const xBuf = dev.createBuffer({ size:xData.byteLength, usage:GPUBufferUsage.STORAGE|GPUBufferUsage.COPY_DST });
    dev.queue.writeBuffer(xBuf, 0, xData);
    const meanBuf = dev.createBuffer({ size:mean.byteLength, usage:GPUBufferUsage.STORAGE|GPUBufferUsage.COPY_DST });
    dev.queue.writeBuffer(meanBuf, 0, mean);
    const sBytes = m*m*4;
    const sBuf = dev.createBuffer({ size:sBytes, usage:GPUBufferUsage.STORAGE|GPUBufferUsage.COPY_SRC });
    const sRead = dev.createBuffer({ size:sBytes, usage:GPUBufferUsage.COPY_DST|GPUBufferUsage.MAP_READ });

    const cacheKey = `__cov_${TS}`;
    if (!this._mmPipelines) this._mmPipelines = {};
    if (!this._mmPipelines[cacheKey]) {
      const module = dev.createShaderModule({ code: GwawCompute._wgslCovariance(TS) });
      this._mmPipelines[cacheKey] = dev.createComputePipeline({ layout:'auto', compute:{ module, entryPoint:'main' } });
    }
    const pipeline = this._mmPipelines[cacheKey];
    const bg = dev.createBindGroup({ layout:pipeline.getBindGroupLayout(0), entries:[
      { binding:0, resource:{ buffer:pBuf } },
      { binding:1, resource:{ buffer:xBuf } },
      { binding:2, resource:{ buffer:meanBuf } },
      { binding:3, resource:{ buffer:sBuf } },
    ]});
    const enc = dev.createCommandEncoder();
    const pass = enc.beginComputePass();
    pass.setPipeline(pipeline); pass.setBindGroup(0, bg);
    const g = Math.ceil(m / TS);
    pass.dispatchWorkgroups(g, g);
    pass.end();
    enc.copyBufferToBuffer(sBuf, 0, sRead, 0, sBytes);
    dev.queue.submit([enc.finish()]);
    await sRead.mapAsync(GPUMapMode.READ);
    const Sigma = new Float32Array(sRead.getMappedRange()).slice();
    sRead.unmap();
    [pBuf,xBuf,meanBuf,sBuf,sRead].forEach(b=>b.destroy());
    return { Sigma, mean, elapsed: performance.now()-t0, backend:`WebGPU(cov tiled ${TS})` };
  }

  /* ========================================================
   *  ポートフォリオ並列評価（Step4-5）
   *  N通りの配分 W(N×m) を一括評価。各スレッドが1配分を担当。
   *  σ=√(wᵀΣw), μp=wᵀμ, sharpe=(μp-rf)/σ
   *  → 行列演算(Σ)と独立並列(N配分)の合流
   *  opts: { backend, rf:0 }
   * ======================================================== */
  async evalPortfolios(Sigma, mu, W, N, m, opts = {}) {
    if (!this._detected) await this.detect();
    const rf = opts.rf || 0;
    const want = opts.backend || (this.capabilities.webgpu ? 'webgpu' : 'cpu');
    if (want === 'webgpu' && this.capabilities.webgpu) {
      try { return await this._evalPortfoliosWebGPU(Sigma, mu, W, N, m, rf, opts); }
      catch (e) { if (opts.allowFallback === false) throw e; return this._evalPortfoliosCPU(Sigma, mu, W, N, m, rf); }
    }
    return this._evalPortfoliosCPU(Sigma, mu, W, N, m, rf);
  }

  _evalPortfoliosCPU(Sigma, mu, W, N, m, rf) {
    const t0 = performance.now();
    const risk=new Float32Array(N), ret=new Float32Array(N), sharpe=new Float32Array(N);
    for (let p=0;p<N;p++){
      const base=p*m;
      let mp=0; for (let i=0;i<m;i++) mp += W[base+i]*mu[i];
      let varp=0;
      for (let i=0;i<m;i++){
        let row=0;
        for (let j=0;j<m;j++) row += W[base+j]*Sigma[i*m+j];
        varp += W[base+i]*row;
      }
      const sd=Math.sqrt(Math.max(0,varp));
      risk[p]=sd; ret[p]=mp; sharpe[p]=sd>1e-9?(mp-rf)/sd:0;
    }
    return { risk, ret, sharpe, elapsed:performance.now()-t0, backend:'CPU' };
  }

  async _evalPortfoliosWebGPU(Sigma, mu, W, N, m, rf, opts) {
    const dev = this.backends.webgpu.device;
    if (!dev) throw new Error('no webgpu device');
    const t0 = performance.now();
    const WG = 64;  // 1ワークグループ64スレッド（各スレッド=1配分）

    const params = new Uint32Array([N, m, 0, 0]);
    const fparams = new Float32Array([rf, 0, 0, 0]);
    const pBuf = dev.createBuffer({ size:32, usage:GPUBufferUsage.UNIFORM|GPUBufferUsage.COPY_DST });
    dev.queue.writeBuffer(pBuf, 0, params);
    dev.queue.writeBuffer(pBuf, 16, fparams);

    const mk = (arr) => { const b=dev.createBuffer({size:arr.byteLength,usage:GPUBufferUsage.STORAGE|GPUBufferUsage.COPY_DST}); dev.queue.writeBuffer(b,0,arr); return b; };
    const sigBuf = mk(Sigma instanceof Float32Array?Sigma:new Float32Array(Sigma));
    const muBuf  = mk(mu instanceof Float32Array?mu:new Float32Array(mu));
    const wBuf   = mk(W instanceof Float32Array?W:new Float32Array(W));
    const outBytes = N*4;
    const riskBuf=dev.createBuffer({size:outBytes,usage:GPUBufferUsage.STORAGE|GPUBufferUsage.COPY_SRC});
    const retBuf =dev.createBuffer({size:outBytes,usage:GPUBufferUsage.STORAGE|GPUBufferUsage.COPY_SRC});
    const shBuf  =dev.createBuffer({size:outBytes,usage:GPUBufferUsage.STORAGE|GPUBufferUsage.COPY_SRC});
    const mkRead=()=>dev.createBuffer({size:outBytes,usage:GPUBufferUsage.COPY_DST|GPUBufferUsage.MAP_READ});
    const riskR=mkRead(), retR=mkRead(), shR=mkRead();

    const cacheKey='__portfolio';
    if (!this._mmPipelines) this._mmPipelines={};
    if (!this._mmPipelines[cacheKey]){
      const module=dev.createShaderModule({ code: GwawCompute._wgslPortfolio(WG) });
      this._mmPipelines[cacheKey]=dev.createComputePipeline({ layout:'auto', compute:{ module, entryPoint:'main' } });
    }
    const pipeline=this._mmPipelines[cacheKey];
    const bg=dev.createBindGroup({ layout:pipeline.getBindGroupLayout(0), entries:[
      { binding:0, resource:{buffer:pBuf} },
      { binding:1, resource:{buffer:sigBuf} },
      { binding:2, resource:{buffer:muBuf} },
      { binding:3, resource:{buffer:wBuf} },
      { binding:4, resource:{buffer:riskBuf} },
      { binding:5, resource:{buffer:retBuf} },
      { binding:6, resource:{buffer:shBuf} },
    ]});
    const enc=dev.createCommandEncoder();
    const pass=enc.beginComputePass();
    pass.setPipeline(pipeline); pass.setBindGroup(0,bg);
    pass.dispatchWorkgroups(Math.ceil(N/WG));
    pass.end();
    enc.copyBufferToBuffer(riskBuf,0,riskR,0,outBytes);
    enc.copyBufferToBuffer(retBuf,0,retR,0,outBytes);
    enc.copyBufferToBuffer(shBuf,0,shR,0,outBytes);
    dev.queue.submit([enc.finish()]);
    await Promise.all([riskR.mapAsync(GPUMapMode.READ),retR.mapAsync(GPUMapMode.READ),shR.mapAsync(GPUMapMode.READ)]);
    const risk=new Float32Array(riskR.getMappedRange()).slice();
    const ret=new Float32Array(retR.getMappedRange()).slice();
    const sharpe=new Float32Array(shR.getMappedRange()).slice();
    riskR.unmap();retR.unmap();shR.unmap();
    [pBuf,sigBuf,muBuf,wBuf,riskBuf,retBuf,shBuf,riskR,retR,shR].forEach(b=>b.destroy());
    return { risk, ret, sharpe, elapsed:performance.now()-t0, backend:'WebGPU(portfolio)' };
  }

  /* ========================================================
   *  エージェントベース市場シミュレーション（Phase4・協調計算）
   *  N体のエージェントが各ステップで注文 → 超過需要を集計 →
   *  価格更新 → 全員が新価格を参照、を steps 回反復。
   *  「スレッド間でデータを回す」協調計算。
   *  agents: { type:Int8Array(N), coef:Float32Array(N), seed:Uint32Array(N) }
   *  opts: { N, steps, lambda, fair, k, backend }
   *  return: { price:Float32Array(steps+1), elapsed, backend }
   * ======================================================== */
  async simulateMarket(agents, opts = {}) {
    if (!this._detected) await this.detect();
    const want = opts.backend || (this.capabilities.webgpu ? 'webgpu' : 'cpu');
    if (want === 'webgpu' && this.capabilities.webgpu) {
      try { return await this._simulateMarketWebGPU(agents, opts); }
      catch (e) { if (opts.allowFallback === false) throw e; return this._simulateMarketCPU(agents, opts); }
    }
    return this._simulateMarketCPU(agents, opts);
  }

  _simulateMarketCPU(agents, opts) {
    const t0 = performance.now();
    const N = opts.N, steps = opts.steps ?? 500;
    const lambda = opts.lambda ?? 1.0, k = opts.k ?? 5;
    const logF = Math.log(opts.fair ?? 100);
    const { type, coef, seed } = agents;
    const state = Uint32Array.from(seed); // PCG状態
    const pcg = (i) => { // 0..1 の乱数（エージェントiの状態を進める）
      let x = state[i]; x = (Math.imul(x,1664525)+1013904223)>>>0; state[i]=x; return x/4294967296;
    };
    const gauss = (i) => { let u=0,v=0; while(u===0)u=pcg(i); while(v===0)v=pcg(i);
      return Math.sqrt(-2*Math.log(u))*Math.cos(2*Math.PI*v); };
    const logP = new Float32Array(steps+1); logP[0]=logF;
    for (let t=0;t<steps;t++){
      const cur=logP[t], trend=cur-logP[Math.max(0,t-k)];
      let D=0;
      for (let i=0;i<N;i++){
        if (type[i]===0)      D += coef[i]*(logF-cur);
        else if (type[i]===1) D += coef[i]*trend;
        else                  D += coef[i]*gauss(i)*0.1;
      }
      let dlog = lambda*(D/N);
      dlog = Math.max(-0.2, Math.min(0.2, dlog));
      logP[t+1]=cur+dlog;
    }
    const price = new Float32Array(steps+1);
    for (let t=0;t<=steps;t++) price[t]=Math.exp(logP[t]);
    return { price, elapsed:performance.now()-t0, backend:'CPU' };
  }

  async _simulateMarketWebGPU(agents, opts) {
    const dev = this.backends.webgpu.device;
    if (!dev) throw new Error('no webgpu device');
    const t0 = performance.now();
    const N = opts.N, steps = opts.steps ?? 500;
    const lambda = opts.lambda ?? 1.0, k = opts.k ?? 5;
    const logF = Math.log(opts.fair ?? 100);
    const SCALE = 1e6;   // floatをi32アトミック加算するためのスケール
    const WG = 256;

    // エージェント状態バッファ
    const typeF = new Float32Array(N); for(let i=0;i<N;i++) typeF[i]=agents.type[i];
    const mk = (arr,usage) => { const b=dev.createBuffer({size:arr.byteLength,usage:usage|GPUBufferUsage.COPY_DST}); dev.queue.writeBuffer(b,0,arr); return b; };
    const typeBuf = mk(typeF, GPUBufferUsage.STORAGE);
    const coefBuf = mk(agents.coef instanceof Float32Array?agents.coef:new Float32Array(agents.coef), GPUBufferUsage.STORAGE);
    const stateBuf= mk(Uint32Array.from(agents.seed), GPUBufferUsage.STORAGE); // PCG状態（更新される）

    // 超過需要アトミック（i32）と価格履歴（logP）
    const demandBuf = dev.createBuffer({ size:4, usage:GPUBufferUsage.STORAGE|GPUBufferUsage.COPY_DST|GPUBufferUsage.COPY_SRC });
    const logPBuf = dev.createBuffer({ size:(steps+1)*4, usage:GPUBufferUsage.STORAGE|GPUBufferUsage.COPY_SRC|GPUBufferUsage.COPY_DST });
    dev.queue.writeBuffer(logPBuf, 0, new Float32Array([logF]));

    // uniform: N,k,step(各stepで更新),_pad / logF,lambda,scale,_pad
    const uParams = dev.createBuffer({ size:32, usage:GPUBufferUsage.UNIFORM|GPUBufferUsage.COPY_DST });
    dev.queue.writeBuffer(uParams, 0, new Uint32Array([N,k,0,0]));
    dev.queue.writeBuffer(uParams, 16, new Float32Array([logF,lambda,SCALE,0]));

    // パイプライン（需要計算・価格更新）
    if (!this._mmPipelines) this._mmPipelines={};
    if (!this._mmPipelines.__mktDemand){
      const m1=dev.createShaderModule({code:GwawCompute._wgslMarketDemand(WG)});
      this._mmPipelines.__mktDemand=dev.createComputePipeline({layout:'auto',compute:{module:m1,entryPoint:'main'}});
      const m2=dev.createShaderModule({code:GwawCompute._wgslMarketPrice()});
      this._mmPipelines.__mktPrice=dev.createComputePipeline({layout:'auto',compute:{module:m2,entryPoint:'main'}});
    }
    const pipeD=this._mmPipelines.__mktDemand, pipeP=this._mmPipelines.__mktPrice;
    const bgD=dev.createBindGroup({layout:pipeD.getBindGroupLayout(0),entries:[
      {binding:0,resource:{buffer:uParams}},{binding:1,resource:{buffer:typeBuf}},
      {binding:2,resource:{buffer:coefBuf}},{binding:3,resource:{buffer:stateBuf}},
      {binding:4,resource:{buffer:demandBuf}},{binding:5,resource:{buffer:logPBuf}},
    ]});
    const bgP=dev.createBindGroup({layout:pipeP.getBindGroupLayout(0),entries:[
      {binding:0,resource:{buffer:uParams}},{binding:4,resource:{buffer:demandBuf}},
      {binding:5,resource:{buffer:logPBuf}},
    ]});

    // ステップ反復：各stepでstepインデックスをuniform更新 → demand=0クリア → 需要 → 価格
    for (let t=0;t<steps;t++){
      dev.queue.writeBuffer(uParams, 8, new Uint32Array([t]));      // step
      dev.queue.writeBuffer(demandBuf, 0, new Int32Array([0]));     // 集計クリア
      const enc=dev.createCommandEncoder();
      const p1=enc.beginComputePass(); p1.setPipeline(pipeD); p1.setBindGroup(0,bgD); p1.dispatchWorkgroups(Math.ceil(N/WG)); p1.end();
      const p2=enc.beginComputePass(); p2.setPipeline(pipeP); p2.setBindGroup(0,bgP); p2.dispatchWorkgroups(1); p2.end();
      dev.queue.submit([enc.finish()]);
    }

    // 価格列を読む
    const readBuf=dev.createBuffer({size:(steps+1)*4,usage:GPUBufferUsage.COPY_DST|GPUBufferUsage.MAP_READ});
    const enc2=dev.createCommandEncoder(); enc2.copyBufferToBuffer(logPBuf,0,readBuf,0,(steps+1)*4); dev.queue.submit([enc2.finish()]);
    await readBuf.mapAsync(GPUMapMode.READ);
    const logP=new Float32Array(readBuf.getMappedRange()).slice(); readBuf.unmap();
    const price=new Float32Array(steps+1); for(let t=0;t<=steps;t++)price[t]=Math.exp(logP[t]);
    [typeBuf,coefBuf,stateBuf,demandBuf,logPBuf,uParams,readBuf].forEach(b=>b.destroy());
    return { price, elapsed:performance.now()-t0, backend:'WebGPU(market coop)' };
  }

  /* ========================================================
   *  価格経路モンテカルロ（Phase5・ハイブリッドのMC部分）
   *  幾何ブラウン運動 S(t+1)=S(t)·exp((mu-σ²/2)dt + σ√dt·Z)
   *  各経路は独立（#1独立並列）。GPUで全経路生成→記録ステップを
   *  間引き保存→CPUでパーセンタイル集計（折衷案）。
   *  opts: { S0, mu, sigma, steps, dt, N, recordPoints, backend }
   *  return: { steps:[{t,vals[5],mean}], percentiles, finalPrices, elapsed, backend }
   * ======================================================== */
  async simulatePaths(opts = {}) {
    if (!this._detected) await this.detect();
    const want = opts.backend || (this.capabilities.webgpu ? 'webgpu' : 'cpu');
    if (want === 'webgpu' && this.capabilities.webgpu) {
      try { return await this._simulatePathsWebGPU(opts); }
      catch (e) { if (opts.allowFallback === false) throw e; return this._simulatePathsCPU(opts); }
    }
    return this._simulatePathsCPU(opts);
  }

  _pctConfig() { return [5,25,50,75,95]; }

  _aggregatePaths(recordVals, recPts, N, S0) {
    // recordVals: Float64Array/Float32Array, レイアウト [recPtIdx*N + pathIdx]
    const pcts = this._pctConfig();
    const steps = [{ t:0, vals:pcts.map(()=>S0), mean:S0 }];
    const tmp = new Float64Array(N);
    for (let r=0;r<recPts.length;r++){
      for (let i=0;i<N;i++) tmp[i]=recordVals[r*N+i];
      tmp.sort();
      let mean=0; for(let i=0;i<N;i++) mean+=tmp[i]; mean/=N;
      const vals=pcts.map(p=>tmp[Math.min(N-1,Math.floor(p/100*N))]);
      steps.push({ t:recPts[r], vals, mean });
    }
    return { steps, percentiles:pcts };
  }

  _simulatePathsCPU(opts) {
    const t0=performance.now();
    const S0=opts.S0??100, mu=opts.mu??0.05, sigma=opts.sigma??0.2;
    const steps=opts.steps??252, N=opts.N??10000, dt=opts.dt??(1/252);
    const seed=opts.seed??12345;
    const recPts = opts.recordPoints || this._defaultRecordPoints(steps);
    let s=seed>>>0;
    const rnd=()=>{s=(Math.imul(s,1664525)+1013904223)>>>0;return s/4294967296;};
    const gauss=()=>{let u=0,v=0;while(u===0)u=rnd();while(v===0)v=rnd();return Math.sqrt(-2*Math.log(u))*Math.cos(2*Math.PI*v);};
    const cur=new Float64Array(N).fill(S0);
    const drift=(mu-sigma*sigma/2)*dt, vol=sigma*Math.sqrt(dt);
    const recSet=new Set(recPts);
    const recordVals=new Float64Array(recPts.length*N);
    const recIndex=new Map(); recPts.forEach((p,i)=>recIndex.set(p,i));
    for (let t=1;t<=steps;t++){
      for (let i=0;i<N;i++) cur[i]*=Math.exp(drift+vol*gauss());
      if (recSet.has(t)){ const ri=recIndex.get(t); for(let i=0;i<N;i++) recordVals[ri*N+i]=cur[i]; }
    }
    const agg=this._aggregatePaths(recordVals, recPts, N, S0);
    const finalPrices=new Float32Array(N); for(let i=0;i<N;i++) finalPrices[i]=cur[i];
    return { ...agg, finalPrices, elapsed:performance.now()-t0, backend:'CPU' };
  }

  _defaultRecordPoints(steps) {
    // 最大50点に間引き
    const maxPts=50; const pts=[];
    if (steps<=maxPts){ for(let t=1;t<=steps;t++) pts.push(t); return pts; }
    const stride=steps/maxPts;
    for (let k=1;k<=maxPts;k++) pts.push(Math.round(k*stride));
    pts[pts.length-1]=steps;
    return pts;
  }

  async _simulatePathsWebGPU(opts) {
    const dev=this.backends.webgpu.device;
    if (!dev) throw new Error('no webgpu device');
    const t0=performance.now();
    const S0=opts.S0??100, mu=opts.mu??0.05, sigma=opts.sigma??0.2;
    const steps=opts.steps??252, N=opts.N??10000, dt=opts.dt??(1/252);
    const seed=opts.seed??12345;
    const recPts=opts.recordPoints || this._defaultRecordPoints(steps);
    const WG=256;

    // 記録ステップ判定用のフラグ配列（steps+1）
    const recFlag=new Int32Array(steps+1).fill(-1);
    recPts.forEach((p,i)=>{ recFlag[p]=i; });
    const recCount=recPts.length;

    const fparams=new Float32Array([S0,(mu-sigma*sigma/2)*dt, sigma*Math.sqrt(dt),0]);
    const uparams=new Uint32Array([N,steps,recCount,seed>>>0]);
    const pf=dev.createBuffer({size:16,usage:GPUBufferUsage.UNIFORM|GPUBufferUsage.COPY_DST}); dev.queue.writeBuffer(pf,0,fparams);
    const pu=dev.createBuffer({size:16,usage:GPUBufferUsage.UNIFORM|GPUBufferUsage.COPY_DST}); dev.queue.writeBuffer(pu,0,uparams);
    const flagBuf=dev.createBuffer({size:(steps+1)*4,usage:GPUBufferUsage.STORAGE|GPUBufferUsage.COPY_DST}); dev.queue.writeBuffer(flagBuf,0,recFlag);
    const outBuf=dev.createBuffer({size:recCount*N*4,usage:GPUBufferUsage.STORAGE|GPUBufferUsage.COPY_SRC});
    const readBuf=dev.createBuffer({size:recCount*N*4,usage:GPUBufferUsage.COPY_DST|GPUBufferUsage.MAP_READ});

    if (!this._mmPipelines) this._mmPipelines={};
    if (!this._mmPipelines.__paths){
      const m=dev.createShaderModule({code:GwawCompute._wgslPaths(WG)});
      this._mmPipelines.__paths=dev.createComputePipeline({layout:'auto',compute:{module:m,entryPoint:'main'}});
    }
    const pipe=this._mmPipelines.__paths;
    const bg=dev.createBindGroup({layout:pipe.getBindGroupLayout(0),entries:[
      {binding:0,resource:{buffer:pf}},{binding:1,resource:{buffer:pu}},
      {binding:2,resource:{buffer:flagBuf}},{binding:3,resource:{buffer:outBuf}},
    ]});
    const enc=dev.createCommandEncoder();
    const pass=enc.beginComputePass(); pass.setPipeline(pipe); pass.setBindGroup(0,bg);
    pass.dispatchWorkgroups(Math.ceil(N/WG)); pass.end();
    enc.copyBufferToBuffer(outBuf,0,readBuf,0,recCount*N*4);
    dev.queue.submit([enc.finish()]);
    await readBuf.mapAsync(GPUMapMode.READ);
    const recordVals=new Float32Array(readBuf.getMappedRange()).slice(); readBuf.unmap();

    const agg=this._aggregatePaths(recordVals, recPts, N, S0);
    // 最終価格分布（最後の記録点）
    const lastBase=(recPts.length-1)*N;
    const finalPrices=recordVals.slice(lastBase,lastBase+N);
    [pf,pu,flagBuf,outBuf,readBuf].forEach(b=>b.destroy());
    return { ...agg, finalPrices, elapsed:performance.now()-t0, backend:'WebGPU(paths)' };
  }
}

// 補助も公開（spec 内 CPU 関数や検証で使えるように）
GwawCompute.PCGRandom = PCGRandom;
GwawCompute.makeSeeds = makeSeeds;

/* ── 行列積 WGSL シェーダー ── */
// naive版：各スレッドが C[row][col] を愚直に内積計算
GwawCompute._WGSL_MATMUL_NAIVE = /* wgsl */ `
struct Dims { M:u32, K:u32, N:u32, _pad:u32, };
@group(0) @binding(0) var<uniform> D : Dims;
@group(0) @binding(1) var<storage, read> A : array<f32>;
@group(0) @binding(2) var<storage, read> B : array<f32>;
@group(0) @binding(3) var<storage, read_write> C : array<f32>;
@compute @workgroup_size(16, 16)
fn main(@builtin(global_invocation_id) gid : vec3<u32>) {
  let col = gid.x; let row = gid.y;
  if (row >= D.M || col >= D.N) { return; }
  var sum : f32 = 0.0;
  for (var k : u32 = 0u; k < D.K; k = k + 1u) {
    sum = sum + A[row * D.K + k] * B[k * D.N + col];
  }
  C[row * D.N + col] = sum;
}`;

// タイル化版（タイルサイズ可変）：TS×TS のタイルを共有メモリに載せる
GwawCompute._wgslMatmulTiled = function(TS) {
  return `
struct Dims { M:u32, K:u32, N:u32, _pad:u32, };
@group(0) @binding(0) var<uniform> D : Dims;
@group(0) @binding(1) var<storage, read> A : array<f32>;
@group(0) @binding(2) var<storage, read> B : array<f32>;
@group(0) @binding(3) var<storage, read_write> C : array<f32>;
const TILE : u32 = ${TS}u;
var<workgroup> Asub : array<array<f32, ${TS}>, ${TS}>;
var<workgroup> Bsub : array<array<f32, ${TS}>, ${TS}>;
@compute @workgroup_size(${TS}, ${TS})
fn main(@builtin(local_invocation_id) lid : vec3<u32>,
        @builtin(workgroup_id) wid : vec3<u32>) {
  let row = wid.y * TILE + lid.y;
  let col = wid.x * TILE + lid.x;
  var sum : f32 = 0.0;
  let numTiles = (D.K + TILE - 1u) / TILE;
  for (var t : u32 = 0u; t < numTiles; t = t + 1u) {
    let aCol = t * TILE + lid.x;
    if (row < D.M && aCol < D.K) { Asub[lid.y][lid.x] = A[row * D.K + aCol]; }
    else { Asub[lid.y][lid.x] = 0.0; }
    let bRow = t * TILE + lid.y;
    if (bRow < D.K && col < D.N) { Bsub[lid.y][lid.x] = B[bRow * D.N + col]; }
    else { Bsub[lid.y][lid.x] = 0.0; }
    workgroupBarrier();
    for (var k : u32 = 0u; k < TILE; k = k + 1u) {
      sum = sum + Asub[lid.y][k] * Bsub[k][lid.x];
    }
    workgroupBarrier();
  }
  if (row < D.M && col < D.N) { C[row * D.N + col] = sum; }
}`;
};

// レジスタブロッキング版：1スレッドが TM×TN の出力ブロックを担当
// ワークグループは (TS/TN)×(TS/TM) スレッド。共有メモリに TS×TS タイルを載せる。
GwawCompute._wgslMatmulRegBlock = function(TS, TM, TN) {
  const wgX = TS / TN, wgY = TS / TM;
  return `
struct Dims { M:u32, K:u32, N:u32, _pad:u32, };
@group(0) @binding(0) var<uniform> D : Dims;
@group(0) @binding(1) var<storage, read> A : array<f32>;
@group(0) @binding(2) var<storage, read> B : array<f32>;
@group(0) @binding(3) var<storage, read_write> C : array<f32>;
const TILE : u32 = ${TS}u;
const TM : u32 = ${TM}u;
const TN : u32 = ${TN}u;
var<workgroup> Asub : array<array<f32, ${TS}>, ${TS}>;
var<workgroup> Bsub : array<array<f32, ${TS}>, ${TS}>;
@compute @workgroup_size(${wgX}, ${wgY})
fn main(@builtin(local_invocation_id) lid : vec3<u32>,
        @builtin(workgroup_id) wid : vec3<u32>) {
  let rowBase = wid.y * TILE + lid.y * TM;
  let colBase = wid.x * TILE + lid.x * TN;
  var acc : array<array<f32, ${TN}>, ${TM}>;
  for (var a:u32=0u;a<TM;a++){ for (var b:u32=0u;b<TN;b++){ acc[a][b]=0.0; } }
  let numTiles = (D.K + TILE - 1u) / TILE;
  for (var t : u32 = 0u; t < numTiles; t = t + 1u) {
    // 共有メモリへロード（各スレッドが TM×TN 要素分担当）
    for (var a:u32=0u;a<TM;a++){
      for (var b:u32=0u;b<TN;b++){
        let lr = lid.y*TM + a; let lc = lid.x*TN + b;
        let gr = wid.y*TILE + lr; let gAc = t*TILE + lc;
        if (gr < D.M && gAc < D.K) { Asub[lr][lc] = A[gr*D.K + gAc]; } else { Asub[lr][lc]=0.0; }
        let bR = t*TILE + lr; let gBc = wid.x*TILE + lc;
        if (bR < D.K && gBc < D.N) { Bsub[lr][lc] = B[bR*D.N + gBc]; } else { Bsub[lr][lc]=0.0; }
      }
    }
    workgroupBarrier();
    for (var k:u32=0u;k<TILE;k++){
      for (var a:u32=0u;a<TM;a++){
        let av = Asub[lid.y*TM + a][k];
        for (var b:u32=0u;b<TN;b++){
          acc[a][b] = acc[a][b] + av * Bsub[k][lid.x*TN + b];
        }
      }
    }
    workgroupBarrier();
  }
  for (var a:u32=0u;a<TM;a++){
    for (var b:u32=0u;b<TN;b++){
      let r = rowBase + a; let c = colBase + b;
      if (r < D.M && c < D.N) { C[r*D.N + c] = acc[a][b]; }
    }
  }
}`;
};

/* 共分散行列 WGSL（タイル化）：Σ[i][j] = (1/(n-ddof)) Σ_t (X[t][i]-μi)(X[t][j]-μj）
   各スレッドが Σ の1要素(i,j)を担当。Xの列i・列jを期間方向に走査して内積。 */
GwawCompute._wgslCovariance = function(TS) {
  return `
struct P { n:u32, m:u32, ddof:u32, _pad:u32, };
@group(0) @binding(0) var<uniform> D : P;
@group(0) @binding(1) var<storage, read> X : array<f32>;
@group(0) @binding(2) var<storage, read> MEAN : array<f32>;
@group(0) @binding(3) var<storage, read_write> S : array<f32>;
@compute @workgroup_size(${TS}, ${TS})
fn main(@builtin(global_invocation_id) gid : vec3<u32>) {
  let i = gid.y;  // 資産 i（行）
  let j = gid.x;  // 資産 j（列）
  if (i >= D.m || j >= D.m) { return; }
  let mi = MEAN[i];
  let mj = MEAN[j];
  var sum : f32 = 0.0;
  for (var t : u32 = 0u; t < D.n; t = t + 1u) {
    let xi = X[t * D.m + i] - mi;
    let xj = X[t * D.m + j] - mj;
    sum = sum + xi * xj;
  }
  let denom = f32(D.n - D.ddof);
  S[i * D.m + j] = sum / denom;
}`;
};

/* ポートフォリオ並列評価 WGSL：各スレッドが1配分 w（W の1行）を評価
   σ=√(wᵀΣw), μp=wᵀμ, sharpe=(μp-rf)/σ */
GwawCompute._wgslPortfolio = function(WG) {
  return `
struct P { N:u32, m:u32, _p0:u32, _p1:u32, rf:f32, _p2:f32, _p3:f32, _p4:f32, };
@group(0) @binding(0) var<uniform> D : P;
@group(0) @binding(1) var<storage, read> SIG : array<f32>;
@group(0) @binding(2) var<storage, read> MU : array<f32>;
@group(0) @binding(3) var<storage, read> W : array<f32>;
@group(0) @binding(4) var<storage, read_write> RISK : array<f32>;
@group(0) @binding(5) var<storage, read_write> RET : array<f32>;
@group(0) @binding(6) var<storage, read_write> SHARPE : array<f32>;
@compute @workgroup_size(${WG})
fn main(@builtin(global_invocation_id) gid : vec3<u32>) {
  let p = gid.x;
  if (p >= D.N) { return; }
  let base = p * D.m;
  // 期待リターン wᵀμ
  var mp : f32 = 0.0;
  for (var i:u32=0u;i<D.m;i++){ mp = mp + W[base+i]*MU[i]; }
  // 分散 wᵀΣw
  var varp : f32 = 0.0;
  for (var i:u32=0u;i<D.m;i++){
    var row : f32 = 0.0;
    for (var j:u32=0u;j<D.m;j++){ row = row + W[base+j]*SIG[i*D.m+j]; }
    varp = varp + W[base+i]*row;
  }
  let sd = sqrt(max(0.0, varp));
  RISK[p] = sd;
  RET[p] = mp;
  if (sd > 1e-9) { SHARPE[p] = (mp - D.rf)/sd; } else { SHARPE[p] = 0.0; }
}`;
};

/* 市場シミュレーション 需要計算カーネル：各エージェントが注文 → 超過需要にアトミック加算
   floatを SCALE 倍して i32 アトミック加算（Phase1のアトミック手法の応用） */
GwawCompute._wgslMarketDemand = function(WG) {
  return `
struct P { N:u32, k:u32, step:u32, _p:u32, logF:f32, lambda:f32, scale:f32, _p2:f32, };
@group(0) @binding(0) var<uniform> D : P;
@group(0) @binding(1) var<storage, read> TYPE : array<f32>;
@group(0) @binding(2) var<storage, read> COEF : array<f32>;
@group(0) @binding(3) var<storage, read_write> STATE : array<u32>;
@group(0) @binding(4) var<storage, read_write> DEMAND : atomic<i32>;
@group(0) @binding(5) var<storage, read> LOGP : array<f32>;
fn pcg(i:u32) -> f32 {
  var x = STATE[i];
  x = x * 1664525u + 1013904223u;
  STATE[i] = x;
  return f32(x) / 4294967296.0;
}
@compute @workgroup_size(${WG})
fn main(@builtin(global_invocation_id) gid : vec3<u32>) {
  let i = gid.x;
  if (i >= D.N) { return; }
  let cur = LOGP[D.step];
  var prevIdx : u32 = 0u;
  if (D.step > D.k) { prevIdx = D.step - D.k; }
  let trend = cur - LOGP[prevIdx];
  let t = TYPE[i];
  var d : f32 = 0.0;
  if (t < 0.5) {
    d = COEF[i] * (D.logF - cur);          // ファンダ（逆張り）
  } else if (t < 1.5) {
    d = COEF[i] * trend;                    // トレンド（順張り）
  } else {
    // ノイズ：Box-Muller
    var u : f32 = pcg(i); if (u < 1e-7) { u = 1e-7; }
    let v : f32 = pcg(i);
    let g = sqrt(-2.0*log(u)) * cos(6.2831853*v);
    d = COEF[i] * g * 0.1;
  }
  atomicAdd(&DEMAND, i32(d * D.scale));
}`;
};

/* 市場シミュレーション 価格更新カーネル：超過需要から新価格を1スレッドで更新 */
GwawCompute._wgslMarketPrice = function() {
  return `
struct P { N:u32, k:u32, step:u32, _p:u32, logF:f32, lambda:f32, scale:f32, _p2:f32, };
@group(0) @binding(0) var<uniform> D : P;
@group(0) @binding(4) var<storage, read_write> DEMAND : atomic<i32>;
@group(0) @binding(5) var<storage, read_write> LOGP : array<f32>;
@compute @workgroup_size(1)
fn main() {
  let totalScaled = atomicLoad(&DEMAND);
  let total = f32(totalScaled) / D.scale;
  var dlog = D.lambda * (total / f32(D.N));
  dlog = clamp(dlog, -0.2, 0.2);
  LOGP[D.step + 1u] = LOGP[D.step] + dlog;
}`;
};

/* 価格経路モンテカルロ WGSL：各スレッド=1経路をGBMで進め、記録ステップで価格を出力
   F: S0, drift=(mu-σ²/2)dt, vol=σ√dt / U: N, steps, recCount, seed */
GwawCompute._wgslPaths = function(WG) {
  return `
struct FP { S0:f32, drift:f32, vol:f32, _p:f32, };
struct UP { N:u32, steps:u32, recCount:u32, seed:u32, };
@group(0) @binding(0) var<uniform> F : FP;
@group(0) @binding(1) var<uniform> U : UP;
@group(0) @binding(2) var<storage, read> FLAG : array<i32>;   // 各ステップの記録インデックス(-1=記録しない)
@group(0) @binding(3) var<storage, read_write> OUT : array<f32>; // [recIdx*N + path]
// PCG乱数状態（経路ごと）
fn nextRand(state: ptr<function, u32>) -> f32 {
  var x = *state;
  x = x * 1664525u + 1013904223u;
  *state = x;
  return f32(x) / 4294967296.0;
}
@compute @workgroup_size(${WG})
fn main(@builtin(global_invocation_id) gid : vec3<u32>) {
  let p = gid.x;
  if (p >= U.N) { return; }
  var rng : u32 = U.seed + p * 2654435761u + 1u;
  var price : f32 = F.S0;
  for (var t : u32 = 1u; t <= U.steps; t = t + 1u) {
    // Box-Muller で標準正規乱数
    var u1 = nextRand(&rng); if (u1 < 1e-7) { u1 = 1e-7; }
    let u2 = nextRand(&rng);
    let z = sqrt(-2.0 * log(u1)) * cos(6.2831853 * u2);
    price = price * exp(F.drift + F.vol * z);
    // 記録ステップなら出力
    let ri = FLAG[t];
    if (ri >= 0) {
      OUT[u32(ri) * U.N + p] = price;
    }
  }
}`;
};





/* エクスポート（global / module 両対応） */
if (typeof module !== 'undefined' && module.exports) module.exports = { GwawCompute, PCGRandom, makeSeeds };
global.GwawCompute = GwawCompute;

})(typeof globalThis !== 'undefined' ? globalThis : this);
